import React from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Space, Select, Checkbox, Row, Col, Tabs } from 'antd';
import { FileTextOutlined, EditOutlined, EyeOutlined, PlusOutlined, DeleteOutlined, HistoryOutlined } from '@ant-design/icons';

import { documentsAPI } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';

const { TextArea } = Input;

const Documents: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [documents, setDocuments] = React.useState<any[]>([]);
  const [detailsVisible, setDetailsVisible] = React.useState(false);
  const [selectedDoc, setSelectedDoc] = React.useState<any | null>(null);
  const [editVisible, setEditVisible] = React.useState(false);
  const [editForm] = Form.useForm();
  const [originalData, setOriginalData] = React.useState<any>(null);
  const [createVisible, setCreateVisible] = React.useState(false);
  const [createForm] = Form.useForm();
  const [searchText, setSearchText] = React.useState('');
  const [activeTab, setActiveTab] = React.useState('documents');
  const [documentVersions, setDocumentVersions] = React.useState<any[]>([]);
  const [selectedDocumentKey, setSelectedDocumentKey] = React.useState<string>('');
  const [selectedRoles, setSelectedRoles] = React.useState<string[]>([]);

  // Начальные тестовые документы
  const defaultDocuments = [
    {
      id: 1,
      key: 'terms',
      title: 'Пользовательское соглашение',
      version: '1.0',
      versionDate: new Date().toISOString(),
      roles: ['admin', 'customer', 'customer_manager', 'supplier', 'employee', 'support', 'delivery'],
      updatedAt: new Date().toISOString(),
      content: `ПОЛЬЗОВАТЕЛЬСКОЕ СОГЛАШЕНИЕ

Последнее обновление: ${new Date().toLocaleString('ru', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })}

1. Общие положения
Данное соглашение регулирует условия использования сервиса "Lunch Ordering System".

2. Права и обязанности пользователя
2.1 Пользователь обязуется использовать сервис в соответствии с законодательством.
2.2 Пользователь гарантирует достоверность предоставленной информации.
2.3 Пользователь несет ответственность за конфиденциальность своих учетных данных.

3. Ответственность
3.1 Компания не несет ответственность за убытки, возникшие при использовании сервиса.
3.2 Компания оставляет право на внесение изменений в сервис без предварительного уведомления.

4. Прекращение доступа
Компания может прекратить доступ пользователя при нарушении условий соглашения.`
    },
    {
      id: 2,
      key: 'privacy',
      title: 'Политика конфиденциальности',
      version: '1.0',
      versionDate: new Date().toISOString(),
      roles: ['admin', 'customer', 'customer_manager', 'supplier', 'employee', 'support', 'delivery'],
      updatedAt: new Date().toISOString(),
      content: `ПОЛИТИКА КОНФИДЕНЦИАЛЬНОСТИ

Последнее обновление: ${new Date().toLocaleString('ru', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })}

1. Информация, которую мы собираем
1.1 Личная информация: имя, email, номер телефона.
1.2 Информация об использовании: данные о действиях в системе.
1.3 Техническая информация: IP адрес, браузер, операционная система.

2. Как мы используем информацию
2.1 Предоставление услуг и поддержки.
2.2 Улучшение качества сервиса.
2.3 Отправка уведомлений и новостей (с согласия пользователя).

3. Защита информации
3.1 Мы используем шифрование для защиты данных.
3.2 Доступ к данным имеют только уполномоченные сотрудники.
3.3 Данные хранятся в защищенных хранилищах.

4. Права пользователя
4.1 Вы можете запросить доступ к своим данным.
4.2 Вы можете запросить удаление своих данных.
4.3 Вы можете возразить на обработку данных.`
    }
  ];

  const load = async () => {
    setLoading(true);
    try {
      const data = await documentsAPI.getDocuments();
      setDocuments(data);
    } catch (e: any) {
      message.error('Ошибка загрузки документов: ' + e.message);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDocumentVersions = async (key: string) => {
    try {
      console.log('🔍 Loading versions for key:', key);
      console.log('🌐 API URL:', `${process.env.REACT_APP_API_URL || 'https://api.luemen.ru'}/api/auth/documents/${key}/versions`);
      
      // Проверяем токен аутентификации
      const token = localStorage.getItem('token');
      console.log('🔑 Token exists:', !!token);
      if (!token) {
        message.error('Необходимо войти в систему');
        return;
      }
      
      const versions = await documentsAPI.getDocumentVersions(key);
      console.log('📋 Received versions:', versions);
      console.log('📊 Versions count:', versions?.length || 0);
      
      if (versions && Array.isArray(versions)) {
        setDocumentVersions(versions);
        setSelectedDocumentKey(key);
        console.log('✅ Versions set successfully');
      } else {
        console.log('⚠️ Versions is not an array:', typeof versions);
        setDocumentVersions([]);
        setSelectedDocumentKey(key);
      }
    } catch (e: any) {
      console.error('❌ Error loading versions:', e);
      console.error('❌ Error details:', e.response?.data || e.message);
      console.error('❌ Error status:', e.response?.status);
      console.error('❌ Error headers:', e.response?.headers);
      message.error('Ошибка загрузки версий: ' + (e.response?.data?.error || e.message));
      setDocumentVersions([]);
    }
  };

  React.useEffect(() => { load(); }, []);

  const showDetails = (doc: any) => {
    setSelectedDoc(doc);
    setDetailsVisible(true);
  };

  const showEdit = (doc: any) => {
    setSelectedDoc(doc);
    setOriginalData({ title: doc.title, content: doc.content, roles: doc.roles });
    setSelectedRoles(doc.roles || []);
    editForm.setFieldsValue({ title: doc.title, content: doc.content });
    setEditVisible(true);
  };

  const handleEdit = async () => {
    try {
      const values = await editForm.validateFields();
      
      // Автоматически обновляем дату "Последнее обновление" в содержимом
      const currentDateTime = new Date().toLocaleString('ru', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      let updatedContent = values.content;
      // Заменяем строку "Последнее обновление" на текущую дату и время
      updatedContent = updatedContent.replace(
        /Последнее обновление:\s*\d{2}\.\d{2}\.\d{4}\s*\d{2}:\d{2}/g,
        `Последнее обновление: ${currentDateTime}`
      );
      
      // Если строка не найдена, добавляем её в начало
      if (!updatedContent.includes('Последнее обновление:')) {
        updatedContent = `Последнее обновление: ${currentDateTime}\n\n${updatedContent}`;
      }
      
      const changes: any = {};
      if (values.title !== originalData.title) changes.title = { old: originalData.title, new: values.title };
      if (updatedContent !== originalData.content) changes.content = { old: originalData.content, new: updatedContent };

      if (Object.keys(changes).length === 0) {
        message.info('Нет изменений');
        return;
      }

      Modal.confirm({
        title: 'Подтвердите изменения',
        content: (
          <div>
            {changes.title && <div><b>Заголовок:</b><br /> {changes.title.old} → {changes.title.new}</div>}
            {changes.content && <div style={{ marginTop: 12 }}><b>Содержимое:</b> изменено</div>}
          </div>
        ),
        okText: 'Сохранить',
        cancelText: 'Отмена',
        async onOk() {
          try {
            const updatedDoc = await documentsAPI.updateDocument(selectedDoc.id, {
              title: values.title,
              content: updatedContent,
              version: `${parseFloat(selectedDoc.version) + 0.1}`.slice(0, 3), // Увеличиваем версию на 0.1
              roles: selectedRoles
            });
            
            setDocuments(prev => prev.map(d => d.id === selectedDoc.id ? updatedDoc : d));
            message.success('Документ обновлён');
            setEditVisible(false);
            load(); // Перезагружаем список документов
          } catch (e: any) {
            message.error('Ошибка обновления: ' + e.message);
          }
        }
      });
    } catch {}
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      
      // key приходит как массив из-за mode="tags", берем первый элемент
      const keyValue = Array.isArray(values.key) ? values.key[0] : values.key;
      
      // Добавляем дату "Последнее обновление" в содержимое нового документа
      const currentDateTime = new Date().toLocaleString('ru', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
      
      const contentWithDate = `Последнее обновление: ${currentDateTime}\n\n${values.content}`;

      const newDoc = await documentsAPI.createDocument({
        title: values.title,
        key: keyValue,
        content: contentWithDate,
        version: values.version || '1.0',
        roles: values.roles || ['employee']
      });
      
      setDocuments(prev => [newDoc, ...prev]);
      message.success(`Документ создан (v${newDoc.version} для ролей: ${newDoc.roles.join(', ')})`);
      setCreateVisible(false);
      createForm.resetFields();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error('Ошибка создания: ' + e.message);
    }
  };

  const handleDelete = (doc: any) => {
    Modal.confirm({
      title: 'Удалить документ',
      content: `Вы уверены, что хотите удалить документ "${doc.title}"?`,
      okText: 'Удалить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk() {
        const updated = documents.filter(d => d.id !== doc.id);
        setDocuments(updated);
        localStorage.setItem('lunch_documents', JSON.stringify(updated));
        message.success('Документ удален');
      }
    });
  };

  const columns = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      render: (id: number) => (
        <span style={{ fontSize: '13px', color: '#666' }}>{id}</span>
      )
    },
    {
      title: 'Название',
      dataIndex: 'title',
      key: 'title',
      width: 250,
      render: (title: string, record: any) => (
        <div
          onClick={() => showDetails(record)}
          style={{
            fontSize: '13px',
            fontWeight: 500,
            color: '#1890ff',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#40a9ff';
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#1890ff';
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {title}
        </div>
      )
    },
    {
      title: 'Ключ',
      dataIndex: 'key',
      key: 'key',
      width: 120,
      render: (key: string, record: any) => (
        <div
          onClick={() => showDetails(record)}
          style={{
            fontSize: '13px',
            fontFamily: 'monospace',
            color: '#1890ff',
            cursor: 'pointer',
            transition: 'all 0.3s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#40a9ff';
            e.currentTarget.style.textDecoration = 'underline';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#1890ff';
            e.currentTarget.style.textDecoration = 'none';
          }}
        >
          {key}
        </div>
      )
    },
    {
      title: 'Версия',
      key: 'version',
      width: 150,
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>v{record.version || '1.0'}</div>
          {record.versionDate && (
            <div style={{ fontSize: '11px', color: '#999' }}>
              {new Date(record.versionDate).toLocaleDateString('ru')}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Обновлено',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 130,
      render: (date: string) => (
        <span style={{ fontSize: '13px', color: '#666' }}>
          {new Date(date).toLocaleDateString('ru')}
        </span>
      ),
      sorter: (a: any, b: any) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Button
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record)}
        />
      )
    }
  ];

  const filteredDocuments = documents.filter(doc =>
    doc.title.toLowerCase().includes(searchText.toLowerCase()) ||
    doc.key.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div style={{ margin: '24px' }}>
      <Card
        title={
          <Space>
            <FileTextOutlined />
            <span>Управление документами</span>
          </Space>
        }
        loading={loading}
        extra={
          <Space>
            <Input
              placeholder="Поиск документов"
              prefix={<EyeOutlined />}
              style={{ width: 200 }}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateVisible(true)}
            >
              Создать документ
            </Button>
          </Space>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'documents',
              label: 'Документы',
              children: (
                <Table
                  rowKey="id"
                  columns={columns as any}
                  dataSource={filteredDocuments}
                  pagination={false}
                  locale={{ emptyText: 'Нет документов' }}
                  scroll={isMobile ? { x: 'max-content' } : undefined}
                  size={isMobile ? 'small' : 'middle'}
                />
              )
            },
            {
              key: 'versions',
              label: (
                <span>
                  <HistoryOutlined />
                  Архив версий
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <Select
                      placeholder="Выберите документ для просмотра версий"
                      style={{ width: '100%' }}
                      onChange={(key) => loadDocumentVersions(key)}
                      value={selectedDocumentKey}
                    >
                      {documents.map(doc => (
                        <Select.Option key={doc.key} value={doc.key}>
                          {doc.title} ({doc.key}) - v{doc.version}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  
                  {documentVersions.length > 0 ? (
                    <Table
                      rowKey="id"
                      dataSource={documentVersions}
                      columns={[
                        {
                          title: 'Версия',
                          dataIndex: 'version',
                          key: 'version',
                          width: 100,
                          render: (version: string, record: any) => (
                            <Space>
                              <span>{version}</span>
                              {record.isActive && <span style={{ color: '#52c41a' }}>●</span>}
                            </Space>
                          )
                        },
                        {
                          title: 'Дата создания',
                          dataIndex: 'createdAt',
                          key: 'createdAt',
                          width: 150,
                          render: (date: string) => new Date(date).toLocaleString('ru')
                        },
                        {
                          title: 'Статус',
                          dataIndex: 'isActive',
                          key: 'isActive',
                          width: 100,
                          render: (isActive: boolean) => (
                            <span style={{ color: isActive ? '#52c41a' : '#999' }}>
                              {isActive ? 'Активная' : 'Архивная'}
                            </span>
                          )
                        },
                        {
                          title: 'Действия',
                          key: 'actions',
                          width: 150,
                          render: (_: any, record: any) => (
                            <Space>
                              <Button
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={() => {
                                  setSelectedDoc(record);
                                  setDetailsVisible(true);
                                }}
                              >
                                Просмотр
                              </Button>
                            </Space>
                          )
                        }
                      ]}
                      scroll={isMobile ? { x: 'max-content' } : undefined}
                      size={isMobile ? 'small' : 'middle'}
                      pagination={false}
                      locale={{ emptyText: 'Нет версий для отображения' }}
                    />
                  ) : (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '40px 20px',
                      color: '#999',
                      fontSize: '14px'
                    }}>
                      {selectedDocumentKey ? 'Нет версий для выбранного документа' : 'Выберите документ для просмотра версий'}
                    </div>
                  )}
                </div>
              )
            }
          ]}
        />

        {/* Модал просмотра */}
        <Modal
          title={selectedDoc?.title}
          open={detailsVisible}
          onCancel={() => setDetailsVisible(false)}
          footer={[
            <Button key="close" onClick={() => setDetailsVisible(false)}>
              Закрыть
            </Button>,
            <Button
              key="edit"
              type="primary"
              icon={<EditOutlined />}
              onClick={() => {
                setDetailsVisible(false);
                showEdit(selectedDoc);
              }}
            >
              Редактировать
            </Button>
          ]}
          width={isMobile ? '100%' : 700}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
          styles={{ body: { padding: '20px' } }}
        >
          {selectedDoc && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ 
                paddingBottom: '16px',
                borderBottom: '1px solid #f0f0f0'
              }}>
                <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>Информация</div>
                <div style={{ display: 'flex', gap: '24px' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>Версия:</div>
                    <div style={{ fontSize: '14px', fontWeight: 500 }}>v{selectedDoc.version}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '4px' }}>Обновлено:</div>
                    <div style={{ fontSize: '14px' }}>{new Date(selectedDoc.updatedAt).toLocaleString('ru')}</div>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: '12px', color: '#999', marginBottom: '8px' }}>Содержимое</div>
                <div style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  padding: '16px',
                  background: '#fafafa',
                  borderRadius: '8px',
                  border: '1px solid #f0f0f0',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {selectedDoc.content}
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* Модал редактирования */}
        <Modal
          title="Редактировать документ"
          open={editVisible}
          onCancel={() => setEditVisible(false)}
          onOk={handleEdit}
          okText="Сохранить"
          cancelText="Отмена"
          width={isMobile ? '100%' : 700}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
          styles={{ body: { padding: '20px' } }}
        >
          <Form form={editForm} layout="vertical">
            <Form.Item
              label="Заголовок"
              name="title"
              rules={[{ required: true, message: 'Введите заголовок' }]}
            >
              <Input placeholder="Название документа" size="large" />
            </Form.Item>
            <Form.Item
              label="Роли"
              name="roles"
            >
              <Select
                mode="multiple"
                placeholder="Выберите роли для документа"
                value={selectedRoles}
                onChange={setSelectedRoles}
                options={[
                  { label: 'Администратор', value: 'admin' },
                  { label: 'Заказчик', value: 'customer' },
                  { label: 'Контролёр заказчика', value: 'customer_manager' },
                  { label: 'Поставщик', value: 'supplier' },
                  { label: 'Сотрудник', value: 'employee' },
                  { label: 'Поддержка', value: 'support' },
                  { label: 'Доставка', value: 'delivery' }
                ]}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              label="Содержимое"
              name="content"
              rules={[{ required: true, message: 'Введите содержимое' }]}
            >
              <TextArea 
                rows={12} 
                placeholder="Текст документа..." 
                style={{ borderRadius: '6px' }}
              />
            </Form.Item>
          </Form>
        </Modal>

        {/* Модал создания */}
        <Modal
          title="Создать новый документ"
          open={createVisible}
          onCancel={() => setCreateVisible(false)}
          onOk={handleCreate}
          okText="Создать"
          cancelText="Отмена"
          width={isMobile ? '100%' : 700}
          centered={!isMobile}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
          styles={{ body: { padding: '20px' } }}
        >
          <Form form={createForm} layout="vertical" initialValues={{ roles: ['employee'] }}>
            <Form.Item
              label="Ключ документа"
              name="key"
              rules={[{ required: true, message: 'Выберите или введите ключ' }]}
            >
              <Select
                placeholder="Выберите или введите новый ключ"
                size="large"
                mode="tags"
                maxCount={1}
                style={{ fontFamily: 'monospace' }}
                options={[
                  { value: 'terms', label: 'terms (Пользовательское соглашение)' },
                  { value: 'privacy', label: 'privacy (Политика конфиденциальности)' },
                  { value: 'employee_terms', label: 'employee_terms (Соглашение сотрудника)' },
                  { value: 'supplier_terms', label: 'supplier_terms (Соглашение поставщика)' },
                  { value: 'customer_terms', label: 'customer_terms (Соглашение заказчика)' },
                ]}
              />
            </Form.Item>
            
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Заголовок"
                  name="title"
                  rules={[{ required: true, message: 'Введите заголовок' }]}
                >
                  <Input placeholder="Название документа" size="large" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Версия"
                  name="version"
                  rules={[{ required: true, message: 'Версия' }]}
                  initialValue="1.0"
                >
                  <Input placeholder="1.0" size="large" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  label="Дата версии"
                  name="versionDate"
                  initialValue={new Date().toLocaleDateString('ru')}
                >
                  <Input size="large" disabled />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="Для каких ролей этот документ обязателен:"
              name="roles"
              rules={[{ required: true, message: 'Выберите хотя бы одну роль' }]}
            >
              <Checkbox.Group style={{ width: '100%' }}>
                <Row>
                  <Col span={8}><Checkbox value="admin">Администратор</Checkbox></Col>
                  <Col span={8}><Checkbox value="customer">Заказчик</Checkbox></Col>
                  <Col span={8}><Checkbox value="supplier">Поставщик</Checkbox></Col>
                  <Col span={8}><Checkbox value="employee">Сотрудник</Checkbox></Col>
                  <Col span={8}><Checkbox value="support">Служба поддержки</Checkbox></Col>
                  <Col span={8}><Checkbox value="delivery">Доставщик</Checkbox></Col>
                </Row>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item
              label="Содержимое"
              name="content"
              rules={[{ required: true, message: 'Введите содержимое' }]}
            >
              <TextArea 
                rows={8} 
                placeholder="Текст документа..." 
                style={{ borderRadius: '6px' }}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Card>
    </div>
  );
};

export default Documents;

